import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p);
const categories = [
  { value: "potong", label: "Potong Rambut" },
  { value: "warna", label: "Hair Coloring" },
  { value: "treatment", label: "Treatment" },
  { value: "styling", label: "Styling" },
];

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", price: "", duration: "30", category: "potong" });

  const fetchServices = useCallback(async () => {
    try {
      const { data } = await api.get("/services/all");
      setServices(data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const openAdd = () => { setEditing(null); setForm({ name: "", description: "", price: "", duration: "30", category: "potong" }); setDialogOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, description: s.description, price: String(s.price), duration: String(s.duration), category: s.category }); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseInt(form.price), duration: parseInt(form.duration) };
    try {
      if (editing) {
        await api.put(`/services/${editing.id}`, payload);
        toast.success("Layanan diperbarui");
      } else {
        await api.post("/services", payload);
        toast.success("Layanan ditambahkan");
      }
      setDialogOpen(false);
      fetchServices();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus layanan ini?")) return;
    try {
      await api.delete(`/services/${id}`);
      toast.success("Layanan dihapus");
      fetchServices();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8" data-testid="services-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Layanan</h1>
          <p className="text-gray-400 text-sm mt-1">Kelola layanan barbershop & salon</p>
        </div>
        <Button onClick={openAdd} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider rounded-none h-10" data-testid="add-service-btn">
          <Plus size={16} className="mr-2" /> Tambah
        </Button>
      </div>

      <div className="bg-[#0F0F0F] border border-[#262626] overflow-x-auto" data-testid="services-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#262626]">
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Nama</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Kategori</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Harga</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Durasi</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Status</th>
              <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.id} className="border-b border-[#262626]/50 hover:bg-[#1A1A1A] transition-colors" data-testid={`service-row-${s.id}`}>
                <td className="px-5 py-3">
                  <p className="text-white font-semibold">{s.name}</p>
                  <p className="text-gray-500 text-xs">{s.description}</p>
                </td>
                <td className="px-5 py-3"><Badge variant="secondary" className="uppercase text-[10px]">{s.category}</Badge></td>
                <td className="px-5 py-3 text-[#E60000] font-semibold">{formatPrice(s.price)}</td>
                <td className="px-5 py-3 text-gray-400">{s.duration} menit</td>
                <td className="px-5 py-3"><Badge className={s.is_active ? "bg-green-900/30 text-green-400" : "bg-red-900/30 text-red-400"}>{s.is_active ? "Aktif" : "Nonaktif"}</Badge></td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(s)} className="p-1.5 text-gray-400 hover:text-white hover:bg-[#262626] transition-colors" data-testid={`edit-service-${s.id}`}><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(s.id)} className="p-1.5 text-gray-400 hover:text-[#E60000] hover:bg-[#262626] transition-colors" data-testid={`delete-service-${s.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase tracking-tight">{editing ? "Edit Layanan" : "Tambah Layanan"}</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">Isi detail layanan barbershop</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="service-form">
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Nama Layanan</Label>
              <Input data-testid="service-name-input" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
            </div>
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Deskripsi</Label>
              <Input data-testid="service-desc-input" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Harga (Rp)</Label>
                <Input data-testid="service-price-input" type="number" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.price} onChange={e => setForm({...form, price: e.target.value})} required />
              </div>
              <div>
                <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Durasi (menit)</Label>
                <Input data-testid="service-duration-input" type="number" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} required />
              </div>
            </div>
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Kategori</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="service-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                  {categories.map(c => <SelectItem key={c.value} value={c.value} className="text-white">{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider h-10 rounded-none" data-testid="service-submit-btn">
              {editing ? "Simpan Perubahan" : "Tambah Layanan"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
